import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
} from 'react';

// Define the structure of the provided data interface
interface AdvisorData {
  // feeModel: string;
  registrationType: string;
  uuid: string | undefined;
  individualdata: {
    gst_number: string;
    masked_pan: string;
    sebiria_number: string;
    years_of_experience: string;
    specialization_areas: string[];
    assest_under_management: string[]; ///according to the new design
    company_name: string;
    office_address: string;
    proof_type: string;
    imageFile: File | null;
    imageFileName: string;
    panFile: File | null;
    panFileName: string;
    proofFile: File | null;
    proofFileName: string;
  };
  charges: {
    free_consultancy: boolean;
    aum_based: boolean;
    hourly_rate: boolean;
    flat_fee: boolean;
    hourly_rate_value: number;
    fee_percentage: number;
    minimum_fee: number;
    flat_fee_value: number;
  };
  profile: {
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    password: string;
    dob: string;
    gender: string;
  };

  activeStep: number; // Add activeStep to the structure
}

// Define the context type interface
interface AdvisorContextType {
  advisorData: AdvisorData;
  setAdvisorData: Dispatch<SetStateAction<AdvisorData>>;
  nextStep: () => void;
  prevStep: () => void;
  setRegistrationType: (registrationType: string) => void;
  setMaskedPan: (masked_pan: string) => void;
  setSebiriaNumber: (sebiria_number: string) => void;
  setYearsOfExperience: (years_of_experience: string) => void; // Changed back to string
  setSpecializationAreas: (specialization_areas: string[]) => void;
  setassestsundermanagement: (assests_under_management: string[]) => void;
  setCompanyName: (company_name: string) => void;
  setOfficeAddress: (officeAddress: string) => void;
  setGst: (gst_number: string) => void;
  setHourlyrate: (hourly_rate: number) => void;
  setFlatfee: (flat_fee: number) => void;
  setFeePercentage: (fee_percentage: number) => void;
  setMinimumfee: (mimimum_fee: number) => void;
  setProfileField: <K extends keyof AdvisorData['profile']>(
    field: K,
    value: AdvisorData['profile'][K],
  ) => void;
  setuuid: (email: string) => void;
  stepOne: () => void;
  stepTwo: () => void;
  stepThree: () => void;
  stepFour: () => void;
  setTrackSubmission: (formName, status) => void;
  trackSubmission;
  setFreeConsultancy: (value: boolean) => void;
  setAumFee: (value: boolean) => void;
  setHourlyCheckBox: (value: boolean) => void;
  setFlatFeeCheckBox: (value: boolean) => void;
  setProofType: (proof_type: string) => void;
  setFile: (file: any) => void;
  setFileName: (fileName: any) => void;
  resetFile: () => void;
  resetFileName: () => void;
  setFileOne: (file: any) => void;
  setFileTwo: (file: any) => void;
  setFileOneName: (fileName: any) => void;
  setFileTwoName: (fileName: any) => void;
}

// Create the default state
const defaultAdvisorData: AdvisorData = {
  // feeModel: 'FIXED_FEE',
  uuid: '',
  registrationType: 'Individual',
  individualdata: {
    gst_number: '',
    masked_pan: '',
    sebiria_number: '',
    years_of_experience: '',
    specialization_areas: [],
    assest_under_management: [],
    company_name: '',
    office_address: '',
    proof_type: '',
    imageFile: null,
    imageFileName: '',
    panFile: null,
    proofFile: null,
    panFileName: '',
    proofFileName: '',
  },
  charges: {
    free_consultancy: false,
    aum_based: false,
    hourly_rate: false,
    flat_fee: false,
    hourly_rate_value: 0,
    fee_percentage: 0,
    minimum_fee: 0,
    flat_fee_value: 0,
  },
  profile: {
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    password: '',
    dob: '2007-03-12',
    gender: 'M',
  },

  activeStep: 1,
};

// Create context
const AdvisorContext = createContext<AdvisorContextType | undefined>(undefined);

// Provider component
const AdvisorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [advisorData, setAdvisorData] =
    useState<AdvisorData>(defaultAdvisorData);
  const [trackSubmission, setSubmission] = useState({
    personalDetails: false,
    professionalDetails: false,
    documentUpload: false,
    imageUpload: false,
  });
  const setTrackSubmission = (formName, status) => {
    setSubmission(prev => ({
      ...prev,
      [formName]: status,
    }));
  };
  const nextStep = () => {
    setAdvisorData(prevState => ({
      ...prevState,
      activeStep: prevState.activeStep + 1,
    }));
  };
  const stepOne = () => {
    setAdvisorData(prevState => ({
      ...prevState,
      activeStep: 1,
    }));
  };
  const stepTwo = () => {
    setAdvisorData(prevState => ({
      ...prevState,
      activeStep: 2,
    }));
  };
  const stepThree = () => {
    setAdvisorData(prevState => ({
      ...prevState,
      activeStep: 3,
    }));
  };
  const stepFour = () => {
    setAdvisorData(prevState => ({
      ...prevState,
      activeStep: 4,
    }));
  };
  const prevStep = () => {
    setAdvisorData(prevState => ({
      ...prevState,
      activeStep: Math.max(prevState.activeStep - 1, 1),
    }));
  };

  const setRegistrationType = (registrationType: string) => {
    setAdvisorData(prevState => ({
      ...prevState,
      registrationType: registrationType,
    }));
  };
  const setIndividualDataField = (
    field: keyof AdvisorData['individualdata'],
    value: any,
  ) => {
    setAdvisorData(prevState => ({
      ...prevState,
      individualdata: { ...prevState.individualdata, [field]: value },
    }));
  };
  const setChargesField = (field: keyof AdvisorData['charges'], value: any) => {
    setAdvisorData(prevState => ({
      ...prevState,
      charges: { ...prevState.charges, [field]: value },
    }));
  };
  // Setters for individual data
  const setGst = (gst_number: string) =>
    setIndividualDataField('gst_number', gst_number);
  const setFileOneName = (fileName: any) =>
    setIndividualDataField('panFileName', fileName);
  const setFileTwoName = (fileName: any) =>
    setIndividualDataField('proofFileName', fileName);
  const setFileOne = (file: any) => setIndividualDataField('panFile', file);
  const setFileTwo = (file: any) => setIndividualDataField('proofFile', file);
  const setFileName = (fileName: any) =>
    setIndividualDataField('imageFileName', fileName);
  const resetFile = () => setIndividualDataField('imageFile', null);
  const resetFileName = () => setIndividualDataField('imageFileName', '');
  const setFile = (file: any) => setIndividualDataField('imageFile', file);
  const setMaskedPan = (masked_pan: string) =>
    setIndividualDataField('masked_pan', masked_pan);
  const setSebiriaNumber = (sebi_ria_number: string) =>
    setIndividualDataField('sebiria_number', sebi_ria_number);
  const setYearsOfExperience = (years_of_experience: string) =>
    setIndividualDataField('years_of_experience', years_of_experience);
  const setassestsundermanagement = (assests_under_management: string[]) =>
    setIndividualDataField('assest_under_management', assests_under_management); // Fixed to string type
  const setSpecializationAreas = (specialization_areas: string[]) =>
    setIndividualDataField('specialization_areas', specialization_areas);
  const setCompanyName = (company_name: string) =>
    setIndividualDataField('company_name', company_name);
  const setProofType = (proof_type: string) =>
    setIndividualDataField('proof_type', proof_type);
  const setOfficeAddress = (officeAddress: string) =>
    setIndividualDataField('office_address', officeAddress);
  const setHourlyrate = (Hourly_rate: number) =>
    setChargesField('hourly_rate_value', Hourly_rate);
  const setFlatfee = (Flat_fee: number) =>
    setChargesField('flat_fee_value', Flat_fee);
  const setFeePercentage = (fee_percentage: number) =>
    setChargesField('fee_percentage', fee_percentage);
  const setMinimumfee = (mimimum_fee: number) =>
    setChargesField('minimum_fee', mimimum_fee);
  const setFreeConsultancy = (value: boolean) =>
    setChargesField('free_consultancy', value);
  const setAumFee = (value: boolean) => setChargesField('aum_based', value);
  const setHourlyCheckBox = (value: boolean) => {
    setChargesField('hourly_rate', value);
  };
  const setFlatFeeCheckBox = (value: boolean) => {
    setChargesField('flat_fee', value);
  };
  const setProfileField = <K extends keyof AdvisorData['profile']>(
    field: K,
    value: AdvisorData['profile'][K],
  ) => {
    setAdvisorData(prevState => ({
      ...prevState,
      profile: { ...prevState.profile, [field]: value },
    }));
  };

  const setuuid = (uuid: string | undefined) => {
    setAdvisorData(prevState => ({
      ...prevState,
      uuid: uuid ?? '', // Use the provided UUID or fallback to an empty string
    }));
  };

  const contextValue: AdvisorContextType = {
    setFileOne,
    setFileTwo,
    setFileOneName,
    setFileTwoName,
    setFileName,
    resetFile,
    resetFileName,
    setFile,
    setProofType,
    setFreeConsultancy,
    setAumFee,
    setHourlyCheckBox,
    setFlatFeeCheckBox,
    trackSubmission,
    setTrackSubmission,
    stepFour,
    stepOne,
    stepThree,
    stepTwo,
    advisorData,
    setAdvisorData,
    nextStep,
    prevStep,
    setRegistrationType,
    setProfileField,
    setMaskedPan,
    setSebiriaNumber,
    setYearsOfExperience,
    setSpecializationAreas,
    setassestsundermanagement,
    setCompanyName,
    setOfficeAddress,
    setGst,
    setHourlyrate,
    setFlatfee,
    setFeePercentage,
    setMinimumfee,
    setuuid,
  };

  return (
    <AdvisorContext.Provider value={contextValue}>
      {children}
    </AdvisorContext.Provider>
  );
};

// Hook to use the context
const useAdvisor = (): AdvisorContextType => {
  const context = useContext(AdvisorContext);
  if (!context) {
    throw new Error('useAdvisor must be used within an AdvisorProvider');
  }
  return context;
};

export { AdvisorProvider, useAdvisor };
